import React, { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";

import Breadcrumbs from "../../../Breadcrumbs/Breadcrumbs.js";
import useSEO from "../../../hooks/useSEO.js";

import specialistsSeoData from "../../../Data/specialists/specialistsSeoData.js";

import RecordPopup from "../../RecordPopup/RecordPopup.js";

import data from "../../../Data/specialists/specialistsData.json";
import publicationsData from "../../../Data/publicationsData1.json";
import publicationsData2 from "../../../Data/publicationsData2.json";
import receptionSchedule from "../../../Data/specialists/receptionSchedule.json";
import publicationsData3 from "../../../Data/publications/publicationsData3.json";

function getSpecialization(person) {
  if (!person || !person.position) return "врач";

  const txt = `${person.position} ${person.department || ""}`.toLowerCase();

  if (txt.includes("дермат")) return "дерматовенеролог";
  if (txt.includes("гинек")) return "гинеколог";
  if (txt.includes("уролог")) return "уролог";
  if (txt.includes("невр")) return "невролог";
  if (txt.includes("терап")) return "терапевт";
  if (txt.includes("ультразв")) return "врач УЗИ";

  return "врач";
}

export default function SpecialistsDetail() {
  const { slug } = useParams();

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showRecordPopup, setShowRecordPopup] = useState(false);

  const [showPublications, setShowPublications] = useState(false);

  const person = data.find((p) => p.slug === slug) || null;

  const isPersonFound = Boolean(person);

  // Уволившийся сотрудник: сохраняем страницу, но отключаем запись
  const isFormerEmployee = Number(person?.id) === 30;

  const spec = getSpecialization(person);

  // ================= NORMALIZE =================
  const normalize = (v) =>
    String(v || "")
      .replace(/\u00A0/g, " ") // важно (NBSP баг!)
      .trim()
      .toLowerCase();

  const normalizedSlug = normalize(person?.slug);

  // ================= PUBLICATIONS =================
  const personPublications = useMemo(() => {
    if (!person) return [];

    const allPublications = [
      ...(publicationsData3 || []),
      ...(publicationsData || []),
      ...(publicationsData2 || []),
    ];

    return allPublications.filter((pub) =>
      (pub.authors || []).some(
        (author) => normalize(author) === normalizedSlug,
      ),
    );
  }, [person, normalizedSlug]);

  // ================= SEO =================
  const seoData = person
    ? specialistsSeoData[person.slug]
    : {
        title: "Сотрудник не найден | УРНИИДВИ",
        description: "Страница специалиста временно недоступна.",
        keywords: "врач, специалист, УРНИИДВИ",
      };

  useSEO({
    title: seoData.title,
    description: seoData.description,
    keywords: seoData.keywords,
    url: person
      ? `/patients/specialists/${person.slug}`
      : "/patients/specialists",
    noindex: !person,
  });

  if (!person) {
    return <p>Сотрудник не найден</p>;
  }

  // ================= SCHEDULE =================
  let doctorSchedule = null;

  receptionSchedule.forEach((section) => {
    const found = section.doctors.find((doctor) => doctor.slug === person.slug);

    if (found) doctorSchedule = found;
  });

  const handleOpenRecord = () => {
    setSelectedDoctor(person);
    setSelectedSchedule(doctorSchedule);
    setShowRecordPopup(true);
  };

  function getYearsText(year) {
    const diff = new Date().getFullYear() - year;

    const lastTwo = diff % 100;
    const lastOne = diff % 10;

    if (lastTwo >= 11 && lastTwo <= 14) return `${diff} лет`;
    if (lastOne === 1) return `${diff} год`;
    if (lastOne >= 2 && lastOne <= 4) return `${diff} года`;

    return `${diff} лет`;
  }

  return (
    <section className="contacts education staff-detail">
      <Breadcrumbs />

      {isFormerEmployee && (
        <div className="staff-detail__former-notice" role="status">
          <div className="staff-detail__former-notice-title">
            <span aria-hidden="true">ℹ️</span>
            Информация о специалисте
          </div>

          <p>
            Врач больше не работает в ГБУ СО «УрНИИДВиИ». Запись на прием к
            данному специалисту в учреждении недоступна.
          </p>

          <div className="staff-detail__former-notice-subtitle">
            Подобрать другого специалиста
          </div>

          <p>
            Перейдите в актуальный список врачей учреждения, чтобы ознакомиться
            с направлениями работы и выбрать специалиста для записи.
          </p>

          <Link
            to="/patients/specialists"
            className="staff-detail__former-link"
          >
            Все специалисты →
          </Link>
        </div>
      )}

      <div className="staff-detail__wrapper">
        <div className="staff-detail__photo">
          <img src={person.photo} alt={`${person.name} ${spec}`} />

          {doctorSchedule && !isFormerEmployee && (
            <button
              type="button"
              className="staff-detail__record"
              onClick={handleOpenRecord}
            >
              Записаться на прием
            </button>
          )}
        </div>

        <div className="staff-detail__info">
          <h1 className="staff-detail__name">{person.name}</h1>
          <h2 className="staff-detail__position">{person.position}</h2>
          <h2 className="staff-detail__position">{person.degree}</h2>

          {person.education && (
            <p className="staff-detail__text">{person.education}</p>
          )}

          {person.documents?.length > 0 && (
            <div className="staff-detail__bio">
              {person.documents.map(
                (doc, index) => doc && <p key={index}>{doc}</p>,
              )}
            </div>
          )}

          <p className="staff-detail__text">
            Стаж работы: {getYearsText(person.graduationYear)}
          </p>

          {/* {person.direction?.length > 0 && (
            <div className="staff-detail__direction">
              <h3 className="staff-detail__position">Направления в работе</h3>

              <ul>
                {person.direction.map((item, index) => (
                  <li key={index}>
                    <p className="staff-detail__text">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          )} */}

          {person.specializations?.length > 0 && (
            <div className="staff-detail__direction">
              <h3 className="staff-detail__position">
                Основные направления работы
              </h3>

              <ul>
                {person.specializations.map((item, index) => (
                  <li key={index}>
                    <p className="staff-detail__text">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {person.diagnostics?.length > 0 && (
            <div className="staff-detail__direction">
              <h3 className="staff-detail__position">Методы диагностики</h3>

              <ul>
                {person.diagnostics.map((item, index) => (
                  <li key={index}>
                    <p className="staff-detail__text">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {person.procedures?.length > 0 && (
            <div className="staff-detail__direction">
              <h3 className="staff-detail__position">Проводимые процедуры</h3>

              <ul>
                {person.procedures.map((item, index) => (
                  <li key={index}>
                    <p className="staff-detail__text">{item}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* POPUP */}
      <RecordPopup
        doctor={selectedDoctor}
        schedule={selectedSchedule}
        show={showRecordPopup}
        onClose={() => setShowRecordPopup(false)}
      />

      {/* ================= PUBLICATIONS ================= */}
      {personPublications.length > 0 && (
        <div className="staff-detail__publications">
          <h4 className="staff-detail__position">Научные публикации:</h4>
          <button
            onClick={() => setShowPublications((p) => !p)}
            className="staff-detail__record"
          >
            {showPublications
              ? "Скрыть публикации"
              : `Показать публикации (${personPublications.length})`}
          </button>

          {showPublications && (
            <div className="staff-detail__publications-list">
              <ul className="staff-detail__publications-list">
                {personPublications.map((pub) => (
                  <li key={pub.id} className="staff-detail__publication">
                    <p>{pub.title}</p>

                    {pub.files?.map((file, i) => (
                      <a
                        key={i}
                        href={file.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="structure__download-link licenses__link"
                      >
                        📄 Открыть документ
                      </a>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
